import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PayMongo Payment Page
 * Redirects to PayMongo and handles return
 * Any pending transaction when returning is automatically cancelled
 */
const PaymongoPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('redirecting'); // redirecting, checking, done
  
  const checkoutUrl = searchParams.get('checkout_url');
  const transactionId = searchParams.get('transaction_id');
  const returnUrl = searchParams.get('return_url') || '/';
  const userType = searchParams.get('user_type') || 'customer';
  
  // Cancel pending transaction
  const cancelPendingTransaction = useCallback(async () => {
    if (!transactionId) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/wallet/topup/cancel`, {
        transaction_id: transactionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Cancel error:', error);
    }
  }, [transactionId]);

  // Check transaction status and handle accordingly
  const checkAndHandleReturn = useCallback(async () => {
    setStatus('checking');
    const token = localStorage.getItem('token');
    
    try {
      // Check transaction status
      const response = await axios.get(`${API}/api/wallet/transaction/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const txStatus = response.data?.status;
      
      if (txStatus === 'completed' || txStatus === 'success') {
        // Payment was successful
        toast.success('Payment successful!');
        const destination = returnUrl.startsWith('/') ? returnUrl : 
          (userType === 'provider' ? '/provider/earnings' : '/wallet');
        navigate(destination + '?payment=success', { replace: true });
      } else {
        // Status is pending or failed - treat as cancelled
        await cancelPendingTransaction();
        toast.info('Payment was cancelled');
        const destination = returnUrl.startsWith('/') ? returnUrl : 
          (userType === 'provider' ? '/provider/earnings' : '/wallet');
        navigate(destination, { replace: true });
      }
    } catch (error) {
      // If we can't check status, cancel and go back
      await cancelPendingTransaction();
      toast.info('Payment was cancelled');
      const destination = returnUrl.startsWith('/') ? returnUrl : 
        (userType === 'provider' ? '/provider/earnings' : '/wallet');
      navigate(destination, { replace: true });
    }
  }, [transactionId, returnUrl, userType, navigate, cancelPendingTransaction]);

  useEffect(() => {
    if (!checkoutUrl) {
      toast.error('Invalid payment session');
      navigate(-1);
      return;
    }
    
    // Check if we're returning from PayMongo (has a payment status in URL or no checkout happening)
    const urlParams = new URLSearchParams(window.location.search);
    const isReturning = urlParams.has('status') || sessionStorage.getItem(`payment_started_${transactionId}`);
    
    if (isReturning && transactionId) {
      // User is returning from PayMongo - check status
      sessionStorage.removeItem(`payment_started_${transactionId}`);
      checkAndHandleReturn();
    } else {
      // Mark that we're starting payment and redirect to PayMongo
      if (transactionId) {
        sessionStorage.setItem(`payment_started_${transactionId}`, 'true');
      }
      
      // Redirect after short delay
      const timer = setTimeout(() => {
        window.location.href = decodeURIComponent(checkoutUrl);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [checkoutUrl, transactionId, navigate, checkAndHandleReturn]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CreditCard className="w-8 h-8 text-blue-600" />
        </div>
        
        {status === 'redirecting' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Redirecting to Payment</h1>
            <p className="text-slate-500 mb-6">Please wait while we connect you to our secure payment partner...</p>
          </>
        )}
        
        {status === 'checking' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Checking Payment Status</h1>
            <p className="text-slate-500 mb-6">Please wait while we verify your payment...</p>
          </>
        )}
        
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        
        {transactionId && (
          <p className="text-slate-400 text-xs mt-6">
            Transaction: {transactionId.slice(0, 8).toUpperCase()}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymongoPayment;
